import React, { useRef, useState, Fragment } from 'react'
import styled from '@emotion/styled'
import { css, useTheme } from '@emotion/react'
import { Router } from '@reach/router'
import { mediaQueries } from '../../theme'
import useScrollIntoView from '../../hooks/use-scroll-into-view'
import { InMemoryStorage } from '../../utils/storage'
import BaseThemeSwitch from './theme-switch'
import useGalleryState from './hooks/use-gallery-state'
import useGalleryData from './hooks/use-gallery-data'
import Heading from './heading'
import BaseShowcase from './showcase'
import BaseSection from './section'
import GroupMenu from './group-menu'
import GalleryContext from './gallery-context'
import useGalleryTheme from './hooks/use-gallery-theme'

const Container = styled.div``

const Menu = styled(GroupMenu)`
  display: none;
  ${mediaQueries.lg} {
    display: block;
  }
`
const GalleryBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  ${mediaQueries.lg} {
    grid-template-columns: 1fr 3fr;
    grid-column-gap: ${p => p.theme.space[14]};
  }
`

const GalleryHeading = styled(Heading)`
  padding: ${p => `0 ${p.theme.space[6]}`};
  margin: 0 0 ${p => p.theme.space[10]};
  font-size: ${p => p.theme.fontSizes[8]};

  ${mediaQueries.md} {
    padding: 0;
  }
`

const Section = styled(BaseSection)`
  padding: ${p => `0 ${p.theme.space[6]}`};

  ${mediaQueries.md} {
    padding: 0;
  }

  ${mediaQueries.lg} {
    padding-top: ${p => p.theme.space[18]};
  }
`

export const Navbar = styled.div`
  ${mediaQueries.lg} {
    position: sticky;
    top: 72px;
    align-self: start;
  }
`

const Showcase = styled(BaseShowcase)`
  ${mediaQueries.lg} {
    padding-top: ${p => p.theme.space[18]};
  }
`

const RouterComponent = ({ children }) => <Fragment>{children}</Fragment>

const ThemeSwitch = ({ theme, onChange }) => {
  const uiTheme = useTheme()
  return (
    <BaseThemeSwitch
      css={css`
        padding: 0 ${uiTheme.space[6]};
        margin-bottom: ${uiTheme.space[12]};
        width: 20rem;

        ${mediaQueries.md} {
          width: 17rem;
          padding: 0;
        }
      `}
      theme={theme}
      onChange={onChange}
    />
  )
}

const GalleryImpl = ({ basePath, withTitle = true, ...rest }) => {
  const { theme, changeTheme } = useGalleryTheme()
  const { templates, groups } = useGalleryData(theme)
  const { activeGroupId, activeGroupName } = useGalleryState(groups)

  const galleryProps = { groups, templates }

  const ref = useRef()
  useScrollIntoView(ref, 16)

  return (
    <Container {...rest}>
      {withTitle && (
        <GalleryHeading subtitle={activeGroupName}>Templates</GalleryHeading>
      )}
      <GalleryBody ref={ref}>
        <Navbar>
          <ThemeSwitch theme={theme} onChange={changeTheme} />
          <Menu groups={groups} activeGroupId={activeGroupId} />
        </Navbar>

        <Router basepath={basePath} component={RouterComponent} primary={false}>
          <Section path="/:groupId/:subgroupId" {...galleryProps} />
          <Section path="/:groupId" {...galleryProps} />
          <Showcase path="/" {...galleryProps} />
        </Router>
      </GalleryBody>
    </Container>
  )
}

const Gallery = props => {
  const [theme, setTheme] = useState(() =>
    InMemoryStorage.getItem('theme', 'light'),
  )

  const changeTheme = theme => {
    InMemoryStorage.setItem('theme', theme)
    setTheme(theme)
  }

  return (
    <GalleryContext.Provider
      value={{
        basePath: props.basePath,
        theme,
        changeTheme,
      }}
    >
      <GalleryImpl {...props} />
    </GalleryContext.Provider>
  )
}

export default Gallery
